// src/utils/subscribeProvider.js

import React, { createContext, useContext, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  onSnapshot,
} from 'firebase/firestore';
import { create } from 'zustand';

const useSubscribeStore = create((set, get) => ({
  subscribed: new Set(),
  subscribers: new Set(),
  subBacks: new Set(),
  isInitialized: false,
  unsubscribe: null,
  unsubscribeDoc: null,

  initialize: () => {
    const { unsubscribe: currentUnsub, unsubscribeDoc: currentDocUnsub } = get();
    if (currentUnsub) {
      currentUnsub();
    }
    if (currentDocUnsub) {
      currentDocUnsub();
    }

    let docUnsub = null;

    const authUnsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (docUnsub) {
          docUnsub();
          docUnsub = null;
        }
        await get().initializeSubscribeData(user.uid);

        // Real-time Firestore listener
        const userRef = doc(db, 'users', user.uid);
        docUnsub = onSnapshot(userRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const subscribed = new Set(data?.subscribed || []);
            const subscribers = new Set(data?.subscribers || []);
            const subBacks = get()._rebuildSubBacks(subscribed, subscribers);
            set({
              subscribed,
              subscribers,
              subBacks,
              isInitialized: true,
            });
          }
        }, (error) => {
          console.error('SubscribeProvider listener error:', error);
        });
      } else {
        set({
          subscribed: new Set(),
          subscribers: new Set(),
          subBacks: new Set(),
          isInitialized: true,
        });
        if (docUnsub) {
          docUnsub();
          docUnsub = null;
        }
      }
    });

    set({
      unsubscribe: () => {
        authUnsubscribe();
        if (docUnsub) {
          docUnsub();
        }
      },
      unsubscribeDoc: () => {
        if (docUnsub) {
          docUnsub();
          docUnsub = null;
        }
      },
    });
  },

  _rebuildSubBacks: (subscribed, subscribers) => {
    const subBacks = new Set();
    for (const id of subscribers) {
      if (!subscribed.has(id)) {
        subBacks.add(id);
      }
    }
    return subBacks;
  },

  initializeSubscribeData: async (userId) => {
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (snap.exists()) {
        const data = snap.data();
        const subscribed = new Set(data?.subscribed || []);
        const subscribers = new Set(data?.subscribers || []);
        const subBacks = get()._rebuildSubBacks(subscribed, subscribers);
        set({
          subscribed,
          subscribers,
          subBacks,
          isInitialized: true,
        });
      }
    } catch (error) {
      console.error('SubscribeProvider init error:', error);
      set({ isInitialized: true });
    }
  },

  toggleSubscribe: async (targetUserId) => {
    const user = auth.currentUser;
    if (!user) return false;

    const { subscribed, subscribers, subBacks } = get();
    const isCurrentlySubscribed = subscribed.has(targetUserId);
    const currentUserRef = doc(db, 'users', user.uid);
    const targetUserRef = doc(db, 'users', targetUserId);

    // Optimistic UI update
    const newSubscribed = new Set(subscribed);
    if (isCurrentlySubscribed) {
      newSubscribed.delete(targetUserId);
    } else {
      newSubscribed.add(targetUserId);
    }

    const newSubBacks = new Set(subBacks);
    if (subscribers.has(targetUserId)) {
      if (isCurrentlySubscribed) {
        newSubBacks.add(targetUserId);
      } else {
        newSubBacks.delete(targetUserId);
      }
    }

    set({
      subscribed: newSubscribed,
      subBacks: newSubBacks,
    });

    try {
      await updateDoc(currentUserRef, {
        subscribed: isCurrentlySubscribed
          ? arrayRemove(targetUserId)
          : arrayUnion(targetUserId),
      });

      await updateDoc(targetUserRef, {
        subscribers: isCurrentlySubscribed
          ? arrayRemove(user.uid)
          : arrayUnion(user.uid),
      });

      // Send subscription notification only when subscribing (not unsubscribing)
      if (!isCurrentlySubscribed && targetUserId !== user.uid) {
        try {
          const { sendSubscribeNotification } = await import(
            '../services/notificationService'
          );
          await sendSubscribeNotification(targetUserId);
        } catch (error) {
          console.error('Failed to send subscribe notification:', error);
        }
      }

      return true;
    } catch (error) {
      // Revert on error
      const revertSubscribed = new Set(subscribed);
      if (isCurrentlySubscribed) {
        revertSubscribed.add(targetUserId);
      } else {
        revertSubscribed.delete(targetUserId);
      }
      const revertSubBacks = get()._rebuildSubBacks(revertSubscribed, subscribers);
      set({
        subscribed: revertSubscribed,
        subBacks: revertSubBacks,
      });
      console.error('SubscribeProvider toggleSubscribe error:', error);
      return false;
    }
  },

  isSubscribedTo: (userId) => {
    return get().subscribed.has(userId);
  },

  isSubscriber: (userId) => {
    return get().subscribers.has(userId);
  },

  shouldSubBack: (userId) => {
    return get().subBacks.has(userId);
  },

  cleanup: () => {
    const { unsubscribe, unsubscribeDoc } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    if (unsubscribeDoc) {
      unsubscribeDoc();
    }
    set({
      subscribed: new Set(),
      subscribers: new Set(),
      subBacks: new Set(),
      isInitialized: false,
      unsubscribe: null,
      unsubscribeDoc: null,
    });
  },
}));

// React Hook for using the subscribe store
export const useSubscribe = () => {
  const store = useSubscribeStore();
  return {
    subscribed: store.subscribed,
    subscribers: store.subscribers,
    subBacks: store.subBacks,
    isInitialized: store.isInitialized,
    toggleSubscribe: store.toggleSubscribe,
    isSubscribedTo: store.isSubscribedTo,
    isSubscriber: store.isSubscriber,
    shouldSubBack: store.shouldSubBack,
    initialize: store.initialize,
    cleanup: store.cleanup,
  };
};

// React Hook for subscription status of a specific user
export const useUserSubscription = (userId) => {
  const { isSubscribedTo, toggleSubscribe, isInitialized } = useSubscribe();
  return {
    isSubscribed: isSubscribedTo(userId),
    toggleSubscribe: () => toggleSubscribe(userId),
    isInitialized,
  };
};

// Context Provider setup
const SubscribeContext = createContext(null);

export const SubscribeProvider = ({ children }) => {
  const store = useSubscribeStore();

  useEffect(() => {
    store.initialize();
    return () => {
      store.cleanup();
    };
  }, []);

  if (!store.isInitialized) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <SubscribeContext.Provider value={store}>
      {children}
    </SubscribeContext.Provider>
  );
};

export const useSubscribeContext = () => {
  const context = useContext(SubscribeContext);
  if (!context) {
    throw new Error('useSubscribeContext must be used within SubscribeProvider');
  }
  return context;
};

export default {
  SubscribeProvider,
  useSubscribe,
  useSubscribeContext,
  useUserSubscription,
};